import React, { useEffect, useState } from "react";
import { Download, FileWarning, Loader2 } from "lucide-react";
import Papa from "papaparse";
import * as XLSXLib from "xlsx";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

const MAX_PREVIEW_ROWS = 200;

const extOf = (name) => {
  const n = (name || "").toLowerCase();
  return n.includes(".") ? "." + n.split(".").pop() : "";
};

const kindOf = (ext) => {
  if (ext === ".pdf") return "pdf";
  if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) return "image";
  if (ext === ".csv") return "csv";
  if ([".xlsx", ".xls"].includes(ext)) return "excel";
  return "none";
};

const PreviewTable = ({ rows, truncated, note }) => (
  <div className="space-y-2">
    {(truncated || note) && (
      <div className="text-xs text-text-secondary bg-subtle rounded-md px-3 py-2" data-testid="preview-partial-notice">
        {note ? `${note} ` : ""}
        {truncated ? `Anteprima parziale (prime ${MAX_PREVIEW_ROWS} righe) — scarica per il file completo.` : ""}
      </div>
    )}
    <div className="overflow-auto max-h-[65vh] border border-border rounded-md" data-testid="preview-table-wrap">
      <table className="text-xs w-full border-collapse">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i === 0 ? "bg-subtle font-medium sticky top-0" : "odd:bg-surface"}>
              {row.map((cell, j) => (
                <td key={j} className="border border-border px-2 py-1 whitespace-nowrap max-w-[280px] truncate">
                  {/* Contenuti SEMPRE come testo (React escapa): mai HTML interpretato */}
                  {cell === null || cell === undefined ? "" : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Anteprima inline di un allegato/documento. Usa la STESSA route del download
 * (con ?inline=1): stessa auth, stesso isolamento tenant, stessi feature flag.
 * PDF → viewer nativo (iframe blob) · immagini → <img> · CSV → tabella (PapaParse)
 * · XLSX/XLS → tabella (SheetJS, prime N righe) · altro → download.
 */
export const FilePreviewModal = ({ open, onClose, filename, fetchPath, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [rows, setRows] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [note, setNote] = useState("");

  const kind = kindOf(extOf(filename));

  useEffect(() => {
    if (!open) return;
    let url = null;
    let cancelled = false;
    setLoading(true); setError(null); setRows(null); setBlobUrl(null); setTruncated(false); setNote("");

    if (kind === "none") { setLoading(false); return; }

    api.get(fetchPath, { responseType: "arraybuffer" })
      .then((res) => {
        if (cancelled) return;
        const buf = res.data;
        const ct = res.headers?.["content-type"] || "application/octet-stream";
        if (kind === "pdf" || kind === "image") {
          url = URL.createObjectURL(new Blob([buf], { type: ct }));
          setBlobUrl(url);
        } else if (kind === "csv") {
          const text = new TextDecoder("utf-8").decode(buf);
          const parsed = Papa.parse(text.trim(), { preview: MAX_PREVIEW_ROWS + 1 });
          const data = parsed.data || [];
          setTruncated(data.length > MAX_PREVIEW_ROWS);
          setRows(data.slice(0, MAX_PREVIEW_ROWS));
        } else if (kind === "excel") {
          const wb = XLSXLib.read(new Uint8Array(buf), { type: "array", sheetRows: MAX_PREVIEW_ROWS + 1 });
          const names = wb.SheetNames || [];
          const ws = wb.Sheets[names[0]];
          const data = ws ? XLSXLib.utils.sheet_to_json(ws, { header: 1, defval: "" }) : [];
          setTruncated(data.length > MAX_PREVIEW_ROWS);
          setRows(data.slice(0, MAX_PREVIEW_ROWS));
          if (names.length > 1) setNote(`Mostrato il primo foglio «${names[0]}» (${names.length} fogli totali).`);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        let detail = "Impossibile caricare l'anteprima.";
        try {
          const parsed = JSON.parse(new TextDecoder("utf-8").decode(err?.response?.data));
          if (parsed?.detail && typeof parsed.detail === "string") detail = parsed.detail;
        } catch { /* keep default */ }
        setError(detail);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchPath]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl w-[95vw] p-4 sm:p-6" data-testid="file-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-base truncate pr-8" title={filename}>{filename}</DialogTitle>
          <DialogDescription className="sr-only">Anteprima documento</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="preview-loading">
            <Loader2 size={24} className="animate-spin text-text-muted" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-12 space-y-3" data-testid="preview-error">
            <FileWarning size={28} className="mx-auto text-text-muted" strokeWidth={1.5} />
            <div className="text-sm text-text-secondary">{error}</div>
          </div>
        )}

        {!loading && !error && kind === "pdf" && blobUrl && (
          <object data={blobUrl} type="application/pdf" className="w-full h-[70vh] border border-border rounded-md bg-white" data-testid="preview-pdf-frame" aria-label={filename}>
            <div className="text-center py-16 space-y-3">
              <FileWarning size={28} className="mx-auto text-text-muted" strokeWidth={1.5} />
              <div className="text-sm text-text-secondary">
                Il tuo browser non supporta l'anteprima PDF integrata.
                Usa «Scarica» per aprire il file.
              </div>
            </div>
          </object>
        )}

        {!loading && !error && kind === "image" && blobUrl && (
          <div className="overflow-auto max-h-[70vh] text-center">
            <img src={blobUrl} alt={filename} className="max-w-full max-h-[68vh] mx-auto object-contain rounded-md" data-testid="preview-image" />
          </div>
        )}

        {!loading && !error && (kind === "csv" || kind === "excel") && rows && (
          rows.length > 0
            ? <PreviewTable rows={rows} truncated={truncated} note={note} />
            : <div className="text-sm text-text-secondary text-center py-10" data-testid="preview-empty">File vuoto o non leggibile.</div>
        )}

        {!loading && kind === "none" && (
          <div className="text-center py-12 space-y-3" data-testid="preview-unsupported">
            <FileWarning size={28} className="mx-auto text-text-muted" strokeWidth={1.5} />
            <div className="text-sm text-text-secondary">Anteprima non disponibile per questo formato.</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          {onDownload && (
            <Button variant="outline" size="sm" onClick={onDownload} data-testid="preview-download-btn">
              <Download size={14} className="mr-1.5" /> Scarica
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="preview-close-btn">Chiudi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
